import { onMounted, onUnmounted } from "vue"

type VersionInfo = {
  version: string
  buildTime: string
}

type UseVersionCheckOptions = {
  /**
   * Callback wywoływany, gdy wykryta zostanie nowa wersja aplikacji.
   * Wywoływany tylko raz (po wykryciu zmiany).
   */
  onNewVersionAvailable: (remote: VersionInfo, current: VersionInfo) => void
  /**
   * Interwał odpytywania w milisekundach.
   * Domyślnie 60 sekund.
   */
  intervalMs?: number
  /**
   * Czy mechanizm ma być aktywny.
   * Domyślnie true.
   */
  enabled?: boolean
}

export function useVersionCheck(options: UseVersionCheckOptions) {
  const { onNewVersionAvailable, intervalMs = 60_000, enabled = true } = options

  let currentVersion: VersionInfo | null = null
  let timer: ReturnType<typeof setInterval> | null = null
  let newVersionDetected = false

  const fetchVersion = async (): Promise<VersionInfo | null> => {
    try {
      const response = await fetch(`/version.json?ts=${Date.now()}`, {
        cache: "no-store",
      })

      if (!response.ok) {
        // eslint-disable-next-line no-console
        console.warn("⚠️ [useVersionCheck] Failed to fetch version.json:", response.status)
        return null
      }

      const data = (await response.json()) as Partial<VersionInfo>

      if (!data.version || !data.buildTime) {
        // eslint-disable-next-line no-console
        console.warn("⚠️ [useVersionCheck] Invalid version.json format:", data)
        return null
      }

      return {
        version: data.version,
        buildTime: data.buildTime,
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("⚠️ [useVersionCheck] Error fetching version.json:", error)
      return null
    }
  }

  const startPolling = () => {
    if (timer) return

    timer = setInterval(async () => {
      if (newVersionDetected) {
        return
      }

      const remoteVersion = await fetchVersion()
      if (!remoteVersion || !currentVersion) {
        return
      }

      if (
        remoteVersion.version !== currentVersion.version ||
        remoteVersion.buildTime !== currentVersion.buildTime
      ) {
        newVersionDetected = true
        onNewVersionAvailable(remoteVersion, currentVersion)

        if (timer) {
          clearInterval(timer)
          timer = null
        }
      }
    }, intervalMs)
  }

  onMounted(async () => {
    if (!enabled) {
      return
    }

    currentVersion = await fetchVersion()

    if (!currentVersion) {
      // Jeśli nie udało się pobrać wersji początkowej, nadal próbujemy w tle
      startPolling()
      return
    }

    startPolling()
  })

  onUnmounted(() => {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  })
}

